import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import Checkbox from "components/v2/Checkbox/Checkbox";
import DatePicker from "components/v2/DatePicker/DatePicker";
import Dropdown from "components/v2/Dropdown/Dropdown";
import Input from "components/v2/Input/Input";
import {
  dangerousGoodsOptions,
  temperatureControlledOptions,
} from "pages/shipper/RequestForm/components/PackingList/PackingListDefaultOptions";

const removeNumberFromDangerousGoodsLabel = (label: string) => label.split(". ")[1] || label;
const getLabelAndSublabelFromTemperatureLabel = (label: string) => {
  const [mainLabel, subLabel] = label.split(/: | at /);
  return { mainLabel, subLabel };
};

interface PackingListSpecialHandlingFormProps {
  index: number;
}

export default function PackingListSpecialHandlingForm({ index }: PackingListSpecialHandlingFormProps) {
  const intl = useIntl();
  const { watch } = useFormContext();

  const isTemperatureControlledOther = watch(`packing_list.${index}.temperature_control_value`) === "6";

  return (
    <>
      <div className="text-secondary text-sm font-medium" style={{ gridColumn: "1 / -1", marginBottom: "-8px" }}>
        <FormattedMessage id="documentParser.uploadPage.reviewDrawer.specialHandling" defaultMessage="Special handling" />
      </div>

      <div className="d-flex flex-column">
        <Checkbox
          name={`packing_list.${index}.temperature_control_required`}
          label={intl.formatMessage({
            id: "new-request.packing-list.temperatureControlled",
            defaultMessage: "Temperature controlled",
          })}
          size="sm"
        />
        <div
          className="mt-md mb-md"
          style={{ display: watch(`packing_list.${index}.temperature_control_required`) ? "block" : "none" }}
        >
          <Dropdown
            options={temperatureControlledOptions(intl).map((option) => {
              const { mainLabel, subLabel } = getLabelAndSublabelFromTemperatureLabel(option.label);
              return {
                ...option,
                label: mainLabel,
                sublabel: subLabel,
              };
            })}
            name={`packing_list.${index}.temperature_control_value`}
            alignRight
          />
        </div>
        {isTemperatureControlledOther && (
          <Input
            name={`packing_list.${index}.temperature_controlled_custom_value`}
            placeholder={intl.formatMessage({
              id: "new-request.packing-list.pleaseProvide",
              defaultMessage: "Please provide",
            })}
            iconRight={<span className="text-secondary-v2 text-md">°C</span>}
          />
        )}
      </div>

      <div className="d-flex flex-column">
        <Checkbox
          name={`packing_list.${index}.is_dangerous_good`}
          label={intl.formatMessage({
            id: "new-request.packing-list.dangerousGoods",
            defaultMessage: "Dangerous goods",
          })}
          size="sm"
        />
        <div className="mt-md" style={{ display: watch(`packing_list.${index}.is_dangerous_good`) ? "block" : "none" }}>
          <Dropdown
            options={dangerousGoodsOptions(intl).map((options) => ({
              ...options,
              label: removeNumberFromDangerousGoodsLabel(options.label),
            }))}
            name={`packing_list.${index}.dangerous_good_value`}
            alignRight
          />
          <Input
            className="mt-md"
            name={`packing_list.${index}.un_number`}
            placeholder={intl.formatMessage({
              id: "new-request.packing-list.unNumber",
              defaultMessage: "UN Number",
            })}
            registerOptions={{ maxLength: 20 }}
          />
        </div>
      </div>

      <div className="d-flex flex-column">
        <Checkbox
          name={`packing_list.${index}.crating_required`}
          label={intl.formatMessage({
            id: "new-request.packing-list.cratingRequired",
            defaultMessage: "Crating required",
          })}
          size="sm"
        />
        <div className="mt-md" style={{ display: watch(`packing_list.${index}.crating_required`) ? "block" : "none" }}>
          <DatePicker
            name={`packing_list.${index}.crating_required_by`}
            selectionMode="single"
            alignment="left"
            numberOfMonths={1}
          />
        </div>
      </div>
    </>
  );
}
